/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, FormEvent, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {PageLink, PageTitle} from '../../../_metronic/layout/core'
import {KTSVG} from '../../../_metronic/helpers'
import {useToast} from '../components/Toats'
import {Loading} from '../components/Loadings/loading'
import {LoadingSpan} from '../components/Loadings/loadingSpan'
import listLayoutStyles from '../components/ListTableLayout/listTableLayout.module.css'
import styles from './publicApi.module.css'
import {
  deletePublicApiToken,
  deletePublicApiWebhook,
  getPublicApiCategories,
  getPublicApiDeliveries,
  getPublicApiEvents,
  getPublicApiInfo,
  getPublicApiScopes,
  getPublicApiSettings,
  getPublicApiTokens,
  getPublicApiWebhooks,
  postPublicApiToken,
  postPublicApiWebhook,
  postPublicApiWebhookTest,
  publicApiErrorMessage,
  PublicApiDelivery,
  PublicApiEvent,
  PublicApiInfo,
  PublicApiScope,
  PublicApiToken,
  PublicApiWebhook,
  putPublicApiSettings,
  SchedulingSettings,
} from '../../services/publicApiService'

/**
 * Integrações > API: tokens da API pública (Nuklie), webhooks de eventos e regras de agenda
 * usadas pelos agentes para marcar visitas/reuniões.
 */

const breadcrumbs: Array<PageLink> = [
  {title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false},
  {title: '', path: '', isSeparator: true, isActive: false},
]

const modalsRoot = document.getElementById('root-modals') || document.body

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString('pt-BR', {dateStyle: 'short', timeStyle: 'short'}) : '-'

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const TASK_STATUSES = [
  {value: 'pending', label: 'Aguardando'},
  {value: 'scheduled', label: 'Agendada'},
  {value: 'in_progress', label: 'Em andamento'},
]

const useCopy = () => {
  const {toggleToast} = useToast()
  const [copied, setCopied] = useState<string | null>(null)
  const copy = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(key)
      setTimeout(() => setCopied(null), 2000)
    } catch (error) {
      toggleToast('Não foi possível copiar.', 'error')
    }
  }
  return {copied, copy}
}

type CopyRowProps = {label: string; value: string; copyKey: string; copied: string | null; onCopy: (value: string, key: string) => void}

const CopyRow: FC<CopyRowProps> = ({label, value, copyKey, copied, onCopy}) => (
  <div className={`${styles.copyBox} mb-3`}>
    <span className={styles.copyLabel}>{label}</span>
    <span className={styles.mono} title={value}>{value}</span>
    <button type='button' className='btn btn-sm btn-light-primary' onClick={() => onCopy(value, copyKey)}>
      {copied === copyKey ? 'Copiado!' : 'Copiar'}
    </button>
  </div>
)

type ConfirmModalProps = {
  show: boolean
  title: string
  text: string
  confirmLabel: string
  loading: boolean
  onConfirm: () => void
  onClose: () => void
}

const ConfirmModal: FC<ConfirmModalProps> = ({show, title, text, confirmLabel, loading, onConfirm, onClose}) =>
  createPortal(
    <Modal dialogClassName='modal-dialog modal-dialog-centered mw-450px' show={show} onHide={onClose} backdrop={true}>
      <div className='modal-body text-center px-10 pt-10 pb-8'>
        <h3 className='fw-bold text-gray-900 mb-2'>{title}</h3>
        <div className='text-gray-600 fs-6 mb-8'>{text}</div>
        <div className='d-flex justify-content-center gap-3'>
          <button className='btn btn-light' onClick={onClose} disabled={loading}>Cancelar</button>
          <button className='btn btn-danger' onClick={onConfirm} disabled={loading}>
            {loading ? <LoadingSpan style={{width: '1.2rem', height: '1.2rem'}} /> : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )

type SecretModalProps = {title: string; secret: string | null; hint: string; onClose: () => void}

// Mostra o token/secret uma única vez: o backend só guarda o hash (token) ou cifrado (secret)
const SecretModal: FC<SecretModalProps> = ({title, secret, hint, onClose}) => {
  const {copied, copy} = useCopy()
  return createPortal(
    <Modal dialogClassName='modal-dialog modal-dialog-centered mw-600px' show={!!secret} onHide={onClose} backdrop='static'>
      <div className='modal-body px-10 pt-10 pb-8'>
        <h3 className='fw-bold text-gray-900 mb-2'>{title}</h3>
        <div className='text-gray-600 fs-6 mb-6'>{hint}</div>
        <div className={`${styles.secretBox} mb-6`}>
          <span className={styles.secretText}>{secret}</span>
          <button type='button' className='btn btn-sm btn-warning' onClick={() => secret && copy(secret, 'secret')}>
            {copied === 'secret' ? 'Copiado!' : 'Copiar'}
          </button>
        </div>
        <div className='alert alert-warning d-flex align-items-center p-4 mb-6'>
          <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-warning me-3' />
          <span className='fs-7'>Este valor não será exibido de novo. Guarde agora em local seguro.</span>
        </div>
        <div className='text-end'>
          <button className='btn btn-primary' onClick={onClose}>Já guardei</button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

// ------------------------------------------------------------------ tokens

const TokensTab: FC<{info: PublicApiInfo | null}> = ({info}) => {
  const {toggleToast} = useToast()
  const {copied, copy} = useCopy()
  const [tokens, setTokens] = useState<PublicApiToken[]>([])
  const [scopes, setScopes] = useState<PublicApiScope[]>([])
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('Nuklie')
  const [selected, setSelected] = useState<string[]>([])
  const [rateLimit, setRateLimit] = useState(120)
  const [submitting, setSubmitting] = useState(false)
  const [newToken, setNewToken] = useState<string | null>(null)
  const [toRevoke, setToRevoke] = useState<PublicApiToken | null>(null)
  const [revoking, setRevoking] = useState(false)

  const load = async () => {
    try {
      setLoading(true)
      const [myTokens, myScopes] = await Promise.all([getPublicApiTokens(), getPublicApiScopes()])
      setTokens(myTokens || [])
      setScopes(myScopes || [])
      setSelected((current) => (current.length ? current : (myScopes || []).map((item) => item.scope)))
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao buscar os tokens!'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const create = async (event: FormEvent) => {
    event.preventDefault()
    try {
      setSubmitting(true)
      const created = await postPublicApiToken({name, scopes: selected, rate_limit_per_minute: rateLimit})
      setNewToken(created.token || null)
      load()
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao gerar o token!'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const revoke = async () => {
    if (!toRevoke) return
    try {
      setRevoking(true)
      await deletePublicApiToken(toRevoke.id)
      toggleToast('Token revogado!', 'success')
      setToRevoke(null)
      load()
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao revogar o token!'), 'error')
    } finally {
      setRevoking(false)
    }
  }

  const toggleScope = (scope: string) =>
    setSelected((current) => (current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope]))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='row g-5 g-xl-8'>
      <div className='col-xl-5'>
        <div className='card card-xl-stretch mb-xl-8'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title d-flex flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>Dados da conexão</span>
              <span className='text-muted fw-semibold fs-7 mt-1'>Envie ao time da Nuklie junto com o token</span>
            </div>
          </div>
          <div className='card-body pt-5'>
            {info ? (
              <>
                <CopyRow label='URL base' value={info.base_url} copyKey='base' copied={copied} onCopy={copy} />
                <CopyRow label='OpenAPI' value={info.openapi_url} copyKey='openapi' copied={copied} onCopy={copy} />
                <CopyRow label='Cabeçalho de usuário' value={info.user_header} copyKey='header' copied={copied} onCopy={copy} />
                <a href={info.docs_url} target='_blank' rel='noreferrer' className='btn btn-sm btn-light-primary mt-2'>
                  <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-3 me-2' />
                  Abrir documentação
                </a>
              </>
            ) : (
              <Loading myClass='spinner-border' />
            )}

            <div className='separator my-8' />

            <form onSubmit={create}>
              <span className='fw-bold fs-4 text-gray-900 d-block mb-5'>Gerar novo token</span>
              <label className='form-label fw-semibold required'>Nome</label>
              <input className='form-control form-control-solid mb-5' value={name} maxLength={120}
                onChange={(e) => setName(e.target.value)} placeholder='Ex.: Nuklie produção' />

              <label className='form-label fw-semibold'>Permissões</label>
              <div className={`${styles.scopeGrid} mb-5`}>
                {scopes.map((item) => (
                  <label key={item.scope} className={styles.scopeItem}>
                    <input type='checkbox' className='form-check-input mt-1' checked={selected.includes(item.scope)}
                      onChange={() => toggleScope(item.scope)} />
                    <span>
                      <span className='fw-bold text-gray-800 d-block fs-7'>{item.scope}</span>
                      <span className='text-muted fs-8'>{item.description}</span>
                    </span>
                  </label>
                ))}
              </div>

              <label className='form-label fw-semibold'>Limite de requisições por minuto</label>
              <input type='number' className='form-control form-control-solid' min={60} max={1200} value={rateLimit}
                onChange={(e) => setRateLimit(Number(e.target.value))} />
              <div className='form-text'>Mínimo 60. Importações grandes usam 100 contatos por página.</div>

              <button type='submit' className='btn btn-primary w-100 mt-8' disabled={submitting || !selected.length || !name.trim()}>
                {submitting ? <LoadingSpan style={{width: '1.2rem', height: '1.2rem'}} /> : 'Gerar token'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className='col-xl-7'>
        <div className={`card card-xl-stretch mb-xl-8 ${listLayoutStyles.listCard}`}>
          <div className='card-header border-0 pt-6'>
            <div className='card-title d-flex flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>Tokens</span>
              <span className='text-muted fw-semibold fs-7 mt-1'>
                {tokens.filter((token) => token.status === 'active').length} ativo(s)
              </span>
            </div>
            <div className='card-toolbar'>
              <button type='button' className='btn btn-sm btn-icon btn-light-primary' onClick={load} disabled={loading} title='Atualizar lista'>
                <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3' />
              </button>
            </div>
          </div>
          <div className={`card-body py-3 ${listLayoutStyles.listTablePanel}`}>
            {loading ? (
              <div className='d-flex justify-content-center py-15'><Loading myClass='spinner-border' /></div>
            ) : tokens.length === 0 ? (
              <div className='text-center text-muted py-15'>Nenhum token gerado ainda.</div>
            ) : (
              <div className={`table-responsive ${listLayoutStyles.listTableWrap}`}>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted fs-7 text-uppercase gs-0'>
                      <th className='min-w-175px'>Token</th>
                      <th className='min-w-100px'>Status</th>
                      <th className='min-w-125px'>Último uso</th>
                      <th className='min-w-100px text-end'>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokens.map((token) => (
                      <tr key={token.id}>
                        <td>
                          <span className='text-gray-900 fw-bold d-block fs-6'>{token.name}</span>
                          <span className='text-muted fs-7 font-monospace'>{token.token_prefix}…</span>
                          <span className='text-muted fs-8 d-block'>{token.scopes.length} permissão(ões) · {token.rate_limit_per_minute}/min</span>
                        </td>
                        <td>
                          <span className={`badge fw-bold ${token.status === 'active' ? 'badge-light-success' : 'badge-light'}`}>
                            {token.status === 'active' ? 'Ativo' : 'Revogado'}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-800 fs-7'>{token.last_used_at ? formatDate(token.last_used_at) : 'Nunca usado'}</span>
                          <span className='text-muted fs-8 d-block'>Criado em {formatDate(token.created_at)}</span>
                        </td>
                        <td className='text-end'>
                          {token.status === 'active' && (
                            <button className='btn btn-sm btn-light-danger' onClick={() => setToRevoke(token)}>Revogar</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <SecretModal
        title='Token gerado'
        secret={newToken}
        hint='Use no cabeçalho Authorization: Bearer <token>. Envie à Nuklie por um canal seguro.'
        onClose={() => setNewToken(null)}
      />
      <ConfirmModal
        show={!!toRevoke}
        title='Revogar token?'
        text={`"${toRevoke?.name}" para de funcionar na hora, e os webhooks cadastrados por ele deixam de receber eventos.`}
        confirmLabel='Revogar'
        loading={revoking}
        onConfirm={revoke}
        onClose={() => !revoking && setToRevoke(null)}
      />
    </div>
  )
}

// ------------------------------------------------------------------ webhooks

const RESOURCE_LABELS: Record<PublicApiEvent['resource'], string> = {
  contact: 'Contatos',
  opportunity: 'Funil comercial',
  task: 'Tarefas (ordens de serviço)',
  ticket: 'Tickets (solicitações de serviço)',
  charge: 'Cobranças',
  subscription: 'Assinaturas',
  webhook: 'Teste',
}

// Ao passar o mouse: quando o evento é enviado, o que vem em data e um exemplo de uso
const EventTooltip: FC<{event?: PublicApiEvent; children: JSX.Element}> = ({event, children}) => {
  if (!event) return children
  return (
    <OverlayTrigger
      placement='right'
      delay={{show: 150, hide: 50}}
      overlay={
        <Tooltip id={`event-${event.event}`} className={styles.eventTooltip}>
          <div className='text-start'>
            <div className='fw-bold fs-7 mb-1'>{event.label}</div>
            <div className='font-monospace fs-8 text-muted mb-2'>{event.event}</div>
            <div className='fs-8 mb-2'><span className='fw-bold'>Quando é enviado: </span>{event.trigger}</div>
            <div className='fs-8 mb-2'><span className='fw-bold'>O que chega em data: </span>{event.data}</div>
            <div className='fs-8'><span className='fw-bold'>Exemplo de uso: </span>{event.use_case}</div>
          </div>
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  )
}


const WebhooksTab: FC = () => {
  const {toggleToast} = useToast()
  const [webhooks, setWebhooks] = useState<PublicApiWebhook[]>([])
  const [events, setEvents] = useState<PublicApiEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [url, setUrl] = useState('')
  const [allEvents, setAllEvents] = useState(true)
  const [selected, setSelected] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [secret, setSecret] = useState<string | null>(null)
  const [toDelete, setToDelete] = useState<PublicApiWebhook | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deliveries, setDeliveries] = useState<{webhook: PublicApiWebhook; items: PublicApiDelivery[]} | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      const [myWebhooks, catalog] = await Promise.all([getPublicApiWebhooks(), getPublicApiEvents()])
      setWebhooks(myWebhooks || [])
      setEvents((catalog || []).filter((item) => item.event !== 'webhook.test'))
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao buscar os webhooks!'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const create = async (event: FormEvent) => {
    event.preventDefault()
    try {
      setSubmitting(true)
      const created = await postPublicApiWebhook({url, events: allEvents ? ['*'] : selected})
      setSecret(created.secret || null)
      setUrl('')
      load()
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao cadastrar o webhook!'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const sendTest = async (webhook: PublicApiWebhook) => {
    try {
      await postPublicApiWebhookTest(webhook.id)
      toggleToast('Evento de teste enfileirado (entrega em até 30 s).', 'success')
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao enviar o teste!'), 'error')
    }
  }

  const showDeliveries = async (webhook: PublicApiWebhook) => {
    try {
      setDeliveries({webhook, items: await getPublicApiDeliveries(webhook.id)})
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao buscar as entregas!'), 'error')
    }
  }

  const remove = async () => {
    if (!toDelete) return
    try {
      setDeleting(true)
      await deletePublicApiWebhook(toDelete.id)
      toggleToast('Webhook removido!', 'success')
      setToDelete(null)
      load()
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao remover o webhook!'), 'error')
    } finally {
      setDeleting(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='row g-5 g-xl-8'>
      <div className='col-xl-4'>
        <div className='card mb-xl-8'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title d-flex flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>Novo webhook</span>
              <span className='text-muted fw-semibold fs-7 mt-1'>A Nuklie normalmente cadastra pela API; use aqui para testes</span>
            </div>
          </div>
          <div className='card-body pt-5'>
            <form onSubmit={create}>
              <label className='form-label fw-semibold required'>URL (https)</label>
              <input className='form-control form-control-solid mb-5' value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder='https://app.nuklie.com/api/v1/integrations/webhooks/…/receive' />
              <label className='form-check form-switch form-check-custom form-check-solid mb-4'>
                <input className='form-check-input' type='checkbox' checked={allEvents} onChange={(e) => setAllEvents(e.target.checked)} />
                <span className='form-check-label fw-semibold text-gray-700'>Todos os eventos</span>
              </label>
              {!allEvents && (
                <div className='mb-4'>
                  <div className='text-muted fs-8 mb-3'>Passe o mouse sobre um evento para ver quando ele é enviado.</div>
                  {(Object.keys(RESOURCE_LABELS) as PublicApiEvent['resource'][])
                    .filter((resource) => events.some((item) => item.resource === resource))
                    .map((resource) => (
                      <div key={resource} className='mb-4'>
                        <div className='fw-bolder text-gray-600 fs-8 text-uppercase mb-2'>{RESOURCE_LABELS[resource]}</div>
                        <div className='d-flex flex-column gap-2'>
                          {events.filter((item) => item.resource === resource).map((item) => (
                            <EventTooltip key={item.event} event={item}>
                              <label className='form-check form-check-custom form-check-solid align-items-start'>
                                <input className='form-check-input mt-1' type='checkbox' checked={selected.includes(item.event)}
                                  onChange={() => setSelected((current) => current.includes(item.event) ? current.filter((e) => e !== item.event) : [...current, item.event])} />
                                <span className='form-check-label'>
                                  <span className='fw-bold text-gray-800 fs-7 d-block'>{item.label}</span>
                                  <span className='text-muted fs-8 font-monospace'>{item.event}</span>
                                </span>
                              </label>
                            </EventTooltip>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}
              <button type='submit' className='btn btn-primary w-100 mt-4' disabled={submitting || !url.trim() || (!allEvents && !selected.length)}>
                {submitting ? <LoadingSpan style={{width: '1.2rem', height: '1.2rem'}} /> : 'Cadastrar webhook'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className='col-xl-8'>
        <div className={`card mb-xl-8 ${listLayoutStyles.listCard}`}>
          <div className='card-header border-0 pt-6'>
            <div className='card-title d-flex flex-column'>
              <span className='fw-bold fs-3 text-gray-900'>Webhooks cadastrados</span>
              <span className='text-muted fw-semibold fs-7 mt-1'>Eventos assinados com HMAC-SHA256; reentrega por até 24 h</span>
            </div>
            <div className='card-toolbar'>
              <button type='button' className='btn btn-sm btn-icon btn-light-primary' onClick={load} disabled={loading} title='Atualizar lista'>
                <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3' />
              </button>
            </div>
          </div>
          <div className={`card-body py-3 ${listLayoutStyles.listTablePanel}`}>
            {loading ? (
              <div className='d-flex justify-content-center py-15'><Loading myClass='spinner-border' /></div>
            ) : webhooks.length === 0 ? (
              <div className='text-center text-muted py-15'>Nenhum webhook cadastrado.</div>
            ) : (
              <div className={`table-responsive ${listLayoutStyles.listTableWrap}`}>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted fs-7 text-uppercase gs-0'>
                      <th className='min-w-250px'>Endereço</th>
                      <th className='min-w-100px'>Entregas</th>
                      <th className='min-w-200px text-end'>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhooks.map((webhook) => (
                      <tr key={webhook.id}>
                        <td>
                          <span className='text-gray-900 fw-bold d-block fs-7 text-break'>{webhook.url}</span>
                          {webhook.events.includes('*') ? (
                            <span className='text-muted fs-8'>Todos os eventos</span>
                          ) : (
                            <div className='d-flex flex-wrap gap-1 mt-1'>
                              {webhook.events.map((name) => (
                                <EventTooltip key={name} event={events.find((item) => item.event === name)}>
                                  <span className='badge badge-light fw-semibold fs-8' style={{cursor: 'help'}}>{name}</span>
                                </EventTooltip>
                              ))}
                            </div>
                          )}
                          {webhook.status !== 'active' && <span className='badge badge-light ms-2'>Desativado</span>}
                        </td>
                        <td>
                          {webhook.failed_count > 0 && <span className='badge badge-light-danger me-1'>{webhook.failed_count} falha(s)</span>}
                          {webhook.pending_count > 0 && <span className='badge badge-light-warning'>{webhook.pending_count} pendente(s)</span>}
                          {!webhook.failed_count && !webhook.pending_count && <span className='badge badge-light-success'>Em dia</span>}
                        </td>
                        <td className='text-end'>
                          <button className='btn btn-sm btn-light me-2' onClick={() => showDeliveries(webhook)}>Entregas</button>
                          <button className='btn btn-sm btn-light-primary me-2' onClick={() => sendTest(webhook)} disabled={webhook.status !== 'active'}>Testar</button>
                          <button className='btn btn-sm btn-light-danger' onClick={() => setToDelete(webhook)}>Remover</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <SecretModal
        title='Webhook cadastrado'
        secret={secret}
        hint='Use este secret para validar o cabeçalho X-SolarVision-Signature: sha256=HMAC-SHA256("{timestamp}.{corpo}").'
        onClose={() => setSecret(null)}
      />
      <ConfirmModal
        show={!!toDelete}
        title='Remover webhook?'
        text='O endereço deixa de receber eventos e as entregas pendentes são descartadas.'
        confirmLabel='Remover'
        loading={deleting}
        onConfirm={remove}
        onClose={() => !deleting && setToDelete(null)}
      />
      {createPortal(
        <Modal dialogClassName='modal-dialog modal-dialog-centered mw-900px' show={!!deliveries} onHide={() => setDeliveries(null)}>
          <div className='modal-header'>
            <h3 className='fw-bold m-0'>Últimas entregas</h3>
          </div>
          <div className='modal-body'>
            {deliveries && deliveries.items.length === 0 ? (
              <div className='text-muted text-center py-10'>Nenhuma entrega ainda.</div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bolder text-muted fs-8 text-uppercase'>
                      <th>Evento</th><th>Status</th><th>Tentativas</th><th>Resposta</th><th>Quando</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries?.items.map((item) => (
                      <tr key={item.id}>
                        <td className='fs-7 fw-bold'>{item.event}</td>
                        <td>
                          <span className={`badge ${item.status === 'delivered' ? 'badge-light-success' : item.status === 'failed' ? 'badge-light-danger' : 'badge-light-warning'}`}>
                            {item.status === 'delivered' ? 'Entregue' : item.status === 'failed' ? 'Falhou' : 'Pendente'}
                          </span>
                        </td>
                        <td className='fs-7'>{item.attempts}</td>
                        <td className='fs-8 text-muted'>{item.last_status_code || item.last_error || '-'}</td>
                        <td className='fs-8 text-muted'>{formatDate(item.delivered_at || item.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>,
        modalsRoot
      )}
    </div>
  )
}

// ------------------------------------------------------------------ agenda

const SchedulingTab: FC = () => {
  const {toggleToast} = useToast()
  const [settings, setSettings] = useState<SchedulingSettings | null>(null)
  const [categories, setCategories] = useState<{id: string; name: string}[]>([])
  const [saving, setSaving] = useState(false)
  // Uma pausa (almoço) editável na tela; a API aceita várias
  const [lunch, setLunch] = useState({start: '', end: ''})

  const load = async () => {
    try {
      const [mySettings, myCategories] = await Promise.all([getPublicApiSettings(), getPublicApiCategories()])
      setSettings(mySettings.scheduling)
      setLunch(mySettings.scheduling.breaks[0] || {start: '', end: ''})
      setCategories(myCategories || [])
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao buscar as regras de agenda!'), 'error')
    }
  }

  const update = (patch: Partial<SchedulingSettings>) => setSettings((current) => (current ? {...current, ...patch} : current))

  const updateCategory = (id: string, patch: {duration_minutes?: number; is_meeting?: boolean}) =>
    setSettings((current) => current ? {
      ...current,
      categories: {...current.categories, [id]: {...(current.categories[id] || {}), ...patch}},
    } : current)

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!settings) return
    try {
      setSaving(true)
      const breaks = lunch.start && lunch.end ? [{start: lunch.start, end: lunch.end}] : []
      const result = await putPublicApiSettings({...settings, breaks})
      setSettings(result.scheduling)
      toggleToast('Regras de agenda salvas!', 'success')
    } catch (error) {
      toggleToast(publicApiErrorMessage(error, 'Erro ao salvar as regras de agenda!'), 'error')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!settings) return <div className='d-flex justify-content-center py-15'><Loading myClass='spinner-border' /></div>

  return (
    <form onSubmit={save} className='card mb-xl-8'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title d-flex flex-column'>
          <span className='fw-bold fs-3 text-gray-900'>Regras de agenda</span>
          <span className='text-muted fw-semibold fs-7 mt-1'>
            Usadas pelos agentes de IA para oferecer horários e agendar ordens de serviço com os colaboradores
          </span>
        </div>
      </div>
      <div className='card-body pt-5'>
        <div className='row g-6'>
          <div className='col-md-6'>
            <label className='form-label fw-semibold'>Dias de atendimento</label>
            <div className={styles.dayGroup}>
              {WEEK_DAYS.map((label, day) => (
                <button key={label} type='button'
                  className={`btn btn-sm ${styles.dayButton} ${settings.work_days.includes(day) ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => update({work_days: settings.work_days.includes(day) ? settings.work_days.filter((d) => d !== day) : [...settings.work_days, day].sort()})}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-semibold'>Início do expediente</label>
            <input type='time' className='form-control form-control-solid' value={settings.work_start} onChange={(e) => update({work_start: e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-semibold'>Fim do expediente</label>
            <input type='time' className='form-control form-control-solid' value={settings.work_end} onChange={(e) => update({work_end: e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-semibold'>Pausa (início)</label>
            <input type='time' className='form-control form-control-solid' value={lunch.start}
              onChange={(e) => setLunch({...lunch, start: e.target.value})} />
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-semibold'>Pausa (fim)</label>
            <input type='time' className='form-control form-control-solid' value={lunch.end}
              onChange={(e) => setLunch({...lunch, end: e.target.value})} />
            <div className='form-text'>Deixe vazio para não ter pausa.</div>
          </div>
          <div className='col-md-2'>
            <label className='form-label fw-semibold'>Intervalo (min)</label>
            <input type='number' min={5} max={240} className='form-control form-control-solid' value={settings.slot_interval_minutes}
              onChange={(e) => update({slot_interval_minutes: Number(e.target.value)})} />
          </div>
          <div className='col-md-2'>
            <label className='form-label fw-semibold'>Duração padrão (min)</label>
            <input type='number' min={5} max={600} className='form-control form-control-solid' value={settings.default_duration_minutes}
              onChange={(e) => update({default_duration_minutes: Number(e.target.value)})} />
          </div>
          <div className='col-md-2'>
            <label className='form-label fw-semibold'>Antecedência (min)</label>
            <input type='number' min={0} max={10080} className='form-control form-control-solid' value={settings.min_notice_minutes}
              onChange={(e) => update({min_notice_minutes: Number(e.target.value)})} />
          </div>
          <div className='col-12'>
            <label className='form-label fw-semibold'>OS que ocupam a agenda do colaborador</label>
            <div className='d-flex flex-wrap gap-5'>
              {TASK_STATUSES.map((status) => (
                <label key={status.value} className='form-check form-check-custom form-check-solid'>
                  <input className='form-check-input' type='checkbox' checked={settings.busy_statuses.includes(status.value)}
                    onChange={() => update({busy_statuses: settings.busy_statuses.includes(status.value)
                      ? settings.busy_statuses.filter((s) => s !== status.value) : [...settings.busy_statuses, status.value]})} />
                  <span className='form-check-label'>{status.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className='separator my-8' />
        <span className='fw-bold fs-4 text-gray-900 d-block mb-2'>Tipos de serviço</span>
        <span className='text-muted fs-7 d-block mb-5'>Duração de cada atendimento e se é reunião (vazio = duração padrão).</span>
        {categories.length === 0 ? (
          <div className='text-muted'>Nenhum tipo de serviço cadastrado no catálogo.</div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted fs-7 text-uppercase'>
                  <th className='min-w-200px'>Tipo</th><th className='w-150px'>Duração (min)</th><th className='w-100px'>Reunião</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => {
                  const config = settings.categories[category.id] || {}
                  return (
                    <tr key={category.id}>
                      <td className='fw-bold text-gray-800'>{category.name}</td>
                      <td>
                        <input type='number' min={5} max={600} className='form-control form-control-sm form-control-solid'
                          placeholder={String(settings.default_duration_minutes)} value={config.duration_minutes || ''}
                          onChange={(e) => updateCategory(category.id, {duration_minutes: e.target.value ? Number(e.target.value) : undefined})} />
                      </td>
                      <td>
                        <label className='form-check form-switch form-check-custom form-check-solid'>
                          <input className='form-check-input' type='checkbox' checked={!!config.is_meeting}
                            onChange={(e) => updateCategory(category.id, {is_meeting: e.target.checked})} />
                        </label>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className='card-footer d-flex justify-content-end'>
        <button type='submit' className='btn btn-primary' disabled={saving}>
          {saving ? <LoadingSpan style={{width: '1.2rem', height: '1.2rem'}} /> : 'Salvar regras'}
        </button>
      </div>
    </form>
  )
}

// ------------------------------------------------------------------ página

const TABS = [
  {key: 'tokens', label: 'Conexão e tokens'},
  {key: 'webhooks', label: 'Webhooks'},
  {key: 'agenda', label: 'Agenda dos agentes'},
] as const

const PublicApiPage: FC = () => {
  const {toggleToast} = useToast()
  const [tab, setTab] = useState<typeof TABS[number]['key']>('tokens')
  const [info, setInfo] = useState<PublicApiInfo | null>(null)

  useEffect(() => {
    getPublicApiInfo().then(setInfo).catch((error) => toggleToast(publicApiErrorMessage(error, 'Erro ao carregar a integração!'), 'error'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder mb-8'>
        {TABS.map((item) => (
          <li key={item.key} className='nav-item'>
            <a className={`nav-link text-active-primary me-6 ${tab === item.key ? 'active' : ''}`} style={{cursor: 'pointer'}}
              onClick={() => setTab(item.key)}>
              {item.label}
            </a>
          </li>
        ))}
      </ul>
      {tab === 'tokens' && <TokensTab info={info} />}
      {tab === 'webhooks' && <WebhooksTab />}
      {tab === 'agenda' && <SchedulingTab />}
    </>
  )
}

const PublicApiWrapper: FC = () => (
  <>
    <PageTitle breadcrumbs={breadcrumbs}>Integração via API (Nuklie)</PageTitle>
    <PublicApiPage />
  </>
)

export {PublicApiWrapper}
